import React from "react";
import * as UI from "./ui.jsx";
import { isLinkSession, logout } from "./auth.js";
import { setSelectedRealm } from "./boot.js";
// App shell — Inglis blue header. Wide desktop: full top nav.
// Compact widths (≤1100px): bottom tabs Home/Stats/Stable/Inspections/Live
// plus a menu sheet for the remaining destinations.
//
// The shell reaches into `auth.js` for two session facts it cannot be told by
// `window.VD`: whether this is a staff access link, and how to end a session.
// Sign-out lives here rather than only on the pre-access screens in `main.jsx`
// because a vendor who has been let in is exactly the one who needs it.
//
// On desktop the two account controls — Profile and sign-out — sit together in
// a menu under the signed-in name, not in the section nav: neither is a section
// of the dashboard, and Profile was the only nav item that was never about the
// sale. Compact widths keep both in the menu sheet for the same reason, and the
// bottom tab bar stays five section destinations.

const { useState: useStateSh, useEffect: useEffectSh } = React;

const NAV = [
  { id: "home",      label: "Home",        icon: "home",      modules: ["foundation.vendor_registry", "foundation.sale_lot_mirror"] },
  { id: "stats",     label: "Stats",       icon: "eye",       modules: ["vendor.stats"] },
  { id: "stable",    label: "Stable",      icon: "horse",     modules: ["foundation.sale_lot_mirror"] },
  { id: "live",      label: "Live",        icon: "cam",       modules: ["live.parade"] },
  { id: "inspect",   label: "Inspections", icon: "clipboard", modules: ["inspections"] },
  { id: "reports",   label: "Reports",     icon: "clipboard", modules: [] },
  { id: "tv",        label: "TV",          icon: "tv",        modules: ["tv"] },
  { id: "admin",     label: "People",      icon: "users",     modules: ["vendor.admin"] },
  { id: "account",   label: "Profile",     icon: "users",     modules: [], always: true },
  { id: "inglis-admin", label: "Inglis Admin", icon: "shield", modules: [] },
];
const MOBILE_TABS = ["home", "stats", "stable", "inspect", "live"];

// Presentation scope: the server decides which sections exist at all
// (VENDORDASH_UI_SECTIONS), and the nav never offers one that is switched off.
const enabled = () => window.VD.sections || [];
// `always` means "every signed-in member has an account to manage". A staff
// access link is not an account: it has no email preferences and no sign-out,
// so Profile would offer a screen with nothing on it.
const navItems = () => NAV.filter(n => (n.always && !isLinkSession()) || enabled().includes(n.id));
const mobileTabs = () => MOBILE_TABS.filter(id => enabled().includes(id)).slice(0, 5);

function useIsMobile() {
  const [m, setM] = useStateSh(() => window.matchMedia("(max-width: 1100px)").matches);
  useEffectSh(() => {
    const mq = window.matchMedia("(max-width: 1100px)");
    const fn = (e) => setM(e.matches);
    mq.addEventListener("change", fn);
    return () => mq.removeEventListener("change", fn);
  }, []);
  return m;
}

// A strip across the top whenever any section on screen is fixture-backed.
// Design work needs the fixtures; nobody should ever mistake them for a real draft.
function MockBar() {
  const mocked = window.VD.mockedModules || [];
  if (mocked.length === 0) return null;
  return (
    <div className="mockbar">
      <span>Mock data · {window.VD.environment}</span>
      <span className="mockbar-list">{mocked.map(UI.moduleLabel).join(" · ")}</span>
    </div>
  );
}

function Shell({ view, setView, title, eyebrow, right, children, onBack, onFeedback }) {
  const { Icon, LiveClock } = UI;
  const mobile = useIsMobile();
  const [menu, setMenu] = useStateSh(false);
  const [account, setAccount] = useStateSh(false);
  const accountRef = React.useRef(null);
  const go = (id) => { setMenu(false); setAccount(false); setView(id); };

  // A menu that cannot be dismissed without choosing from it is a trap, and
  // this one holds sign-out. Both escapes are wired while it is open only.
  useEffectSh(() => {
    if (!account) return undefined;
    const away = (event) => {
      if (accountRef.current && !accountRef.current.contains(event.target)) setAccount(false);
    };
    const key = (event) => { if (event.key === "Escape") setAccount(false); };
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", key);
    return () => {
      document.removeEventListener("mousedown", away);
      document.removeEventListener("keydown", key);
    };
  }, [account]);
  const Ic = ({ n, s = 18 }) => React.createElement(Icon[n], { style: { width: s, height: s } });
  const vendor = window.DATA.VENDOR;
  const sale = window.DATA.SALE;
  const access = window.VD.access || {};
  const realms = access.realms || [];
  const switchRealm = (event) => {
    const realm = event.target.value;
    setSelectedRealm(realm);
    const url = new URL(window.location.href);
    if (realm) url.searchParams.set("realm", realm); else url.searchParams.delete("realm");
    window.history.replaceState({}, "", url.toString());
    window.location.reload();
  };

  return (
    <div className={`app ${mobile ? "mobile" : ""}`}>
      <header className="hdr">
        <div className="hdr-left">
          {window.VD.logo
            ? <img className="hdr-logo" src={window.VD.logo} alt="Inglis"/>
            : <span className="display" style={{ color: "#fff", fontSize: 22, letterSpacing: ".12em" }}>INGLIS</span>}
          {!mobile && (
            <nav className="hdr-nav">
              {navItems().filter(it => it.id !== "account").map(it => (
                <button key={it.id} className={view === it.id ? "active" : ""} onClick={() => go(it.id)}>
                  <Ic n={it.icon}/><span>{it.label}</span>
                </button>
              ))}
            </nav>
          )}
        </div>
        <div className="hdr-right">
          {realms.length > 1 && (
            <label className="realm-switcher">
              <span className="sr-only">Dashboard realm</span>
              <select value={access.realm || ""} onChange={switchRealm} aria-label="Dashboard realm">
                {realms.map((item) => <option key={item.key} value={item.key}>{item.name || item.key}</option>)}
              </select>
            </label>
          )}
          {access.actingAs && (
            <>
              <span className="live-pill">Acting as {access.realmName || access.realm}</span>
              <button type="button" className="btn" aria-label="Return to Inglis"
                onClick={() => switchRealm({ target: { value: "inglis" } })}>
                {mobile ? "Return" : "Return to Inglis"}
              </button>
            </>
          )}
          {!mobile && sale.sells && <span className="live-pill"><span className="pulse"/>Sells {sale.daysOut === 1 ? "tomorrow" : sale.sells}</span>}
          <LiveClock/>
          {!mobile && !isLinkSession() && (
            <div className="hdr-account" ref={accountRef}>
              <button type="button" className={`hdr-account-trigger ${view === "account" ? "active" : ""}`}
                aria-haspopup="menu" aria-expanded={account} aria-label="Your account"
                onClick={() => setAccount(open => !open)}>
                <span className="hdr-user">{vendor.initials || <Ic n="users" s={17}/>}</span>
                {vendor.staff && <span className="hdr-account-name">{vendor.staff}</span>}
                <Ic n="chev" s={15}/>
              </button>
              {account && (
                <div className="hdr-account-menu" role="menu">
                  <button type="button" role="menuitem" className="hdr-account-item"
                    onClick={() => go("account")}>
                    <Ic n="users" s={18}/><span>Profile</span>
                  </button>
                  <button type="button" role="menuitem" className="hdr-account-item"
                    aria-label="Sign out" onClick={() => logout()}>
                    <Ic n="power" s={18}/><span>Sign out</span>
                  </button>
                </div>
              )}
            </div>
          )}
          {mobile && (onFeedback || navItems().some(n => !mobileTabs().includes(n.id))) && (
            <button className="burger" onClick={() => setMenu(true)} aria-label="Menu"><Ic n="burger" s={22}/></button>
          )}
        </div>
      </header>

      <MockBar/>

      <div className="subbar">
        <div className="title-block">
          <div className="crumbs">
            {onBack && (
              <button className="row" onClick={onBack} style={{ color: "var(--inglis-blue)", padding: 0, fontWeight: 700 }}>
                <Icon.back style={{ width: 14, height: 14 }}/> <span>Back</span>
                <span className="dot" style={{ color: "var(--fg-muted)" }}/>
              </button>
            )}
            <span>{eyebrow}</span>
          </div>
          <h1>{title}</h1>
        </div>
        <div className="hdr-right">{right}</div>
      </div>

      <div className="scroll">{children}</div>

      {mobile && mobileTabs().length > 1 && (
        <nav className="tabbar">
          {mobileTabs().map(id => {
            const it = NAV.find(n => n.id === id);
            return (
              <button key={id} className={view === id ? "active" : ""} onClick={() => go(id)}>
                <Ic n={it.icon} s={21}/><span>{it.label}</span>
              </button>
            );
          })}
        </nav>
      )}

      {mobile && menu && (
        <div className="sheet-wrap" onClick={() => setMenu(false)}>
          <div className="sheet" onClick={e => e.stopPropagation()}>
            <div className="row" style={{ justifyContent: "space-between", marginBottom: 14 }}>
              <div>
                <div className="eyebrow">{vendor.name}</div>
                <div style={{ fontSize: 13, color: "var(--fg-muted)", marginTop: 3 }}>{vendor.staff} · {vendor.barn}</div>
              </div>
              <button onClick={() => setMenu(false)} style={{ color: "var(--fg-2)" }}><Ic n="close" s={22}/></button>
            </div>
            {navItems().filter(n => !mobileTabs().includes(n.id)).map(it => (
              <button key={it.id} className={`sheet-item ${view === it.id ? "active" : ""}`} onClick={() => go(it.id)}>
                <Ic n={it.icon} s={20}/><span>{it.label}</span><Icon.arrow style={{ width: 15, height: 15, marginLeft: "auto", opacity: .4 }}/>
              </button>
            ))}
            {/* Feedback is here rather than floating at compact widths, where the
                pill sat over the page and the tab bar (24 September 2026). */}
            {onFeedback && (
              <button type="button" className="sheet-item" onClick={() => { setMenu(false); onFeedback(); }}>
                <Ic n="mail" s={20}/><span>Send feedback</span>
              </button>
            )}
            {!isLinkSession() && (
              <button type="button" className="sheet-item" aria-label="Sign out" onClick={() => logout()}>
                <Ic n="power" s={20}/><span>Sign out</span>
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export { Shell, NAV, useIsMobile };
